/**
 * Privacy Policy Page — /privacy
 * Public route — no auth required.
 * Satisfies DPDPA 2023 §6 disclosure requirement (I4 compliance item).
 */

import React from 'react';

const Privacy = () => (
  <div style={styles.container}>
    <div style={styles.card}>
      <h1 style={styles.heading}>Privacy Policy</h1>
      <p style={styles.meta}>Effective date: 1 April 2026, updated July 2026 &nbsp;|&nbsp; LifeChiAI Platform</p>

      <section style={styles.section}>
        <h2 style={styles.subheading}>1. Who we are</h2>
        <p style={styles.body}>
          LifeChiAI is a digital platform that connects patients with pranic healing centres.
          Your personal data is processed by the healing centre you register with, using the
          LifeChiAI platform operated by LifeChiAI Pvt. Ltd.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.subheading}>2. Data we collect</h2>

        <h3 style={styles.subheading3}>2.1 Patient data</h3>
        <ul style={styles.list}>
          <li>Name, mobile number, and email address (required to create your account)</li>
          <li>Health condition described in your healing request (used to match you with a healer)</li>
          <li>Session records and notes maintained by your healer</li>
          <li>Approximate location (optional, used only for centre discovery)</li>
          <li>Payment records if a fee is charged by your centre</li>
        </ul>

        <h3 style={styles.subheading3}>2.2 Healer data</h3>
        <ul style={styles.list}>
          <li>Name, mobile number, email address, and login credentials</li>
          <li>Professional certifications and training records</li>
          <li>Bank account or UPI ID for payout processing</li>
          <li>PAN number (optional, provided voluntarily for TDS compliance — Section 194J of the Income Tax Act)</li>
        </ul>

        <h3 style={styles.subheading3}>2.3 Centre administrator data</h3>
        <ul style={styles.list}>
          <li>Name, mobile number, email address, and login credentials</li>
          <li>Billing and subscription details for the centre account</li>
          <li>Centre PAN number (optional, provided voluntarily for TDS compliance — Section 194J of the Income Tax Act)</li>
        </ul>

        <h3 style={styles.subheading3}>2.4 What we do not collect</h3>
        <p style={styles.body}>
          We do not collect biometric data, Aadhaar numbers, or passport details. PAN numbers are collected
          only when voluntarily provided by healers or centre administrators for TDS compliance under Section
          194J of the Income Tax Act. PAN numbers are stored with encryption, never shared with third
          parties, and are masked in all user interfaces.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.subheading}>3. Why we collect it (DPDPA 2023 §6)</h2>
        <p style={styles.body}>
          We collect your data only with your explicit consent, given when you submit a healing
          request or register with a centre. Your data is used solely to:
        </p>
        <ul style={styles.list}>
          <li>Match you with an appropriate healer</li>
          <li>Record and track your healing sessions</li>
          <li>Send appointment confirmations and reminders</li>
          <li>Generate invoices if applicable</li>
        </ul>
        <p style={styles.body}>
          We do not sell, rent, or share your data with third parties for marketing purposes.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.subheading}>4. Third-party processors</h2>
        <p style={styles.body}>
          LifeChiAI uses the following third-party services to operate the platform.
          Each receives only the data necessary for its specific function.
        </p>
        {/* Daily.co and Expo omitted — web users do not join video rooms or receive push notifications. Web-only users interact with Anthropic, OpenAI, Razorpay, MongoDB, Render, ElevenLabs, Cloudflare R2, Fast2SMS, Brevo, Apple, Google, and Meta only. */}
        <ul style={styles.list}>
          <li><strong>Anthropic Claude</strong> (USA) — AI assistant for healers and administrators.
            Receives: session context. Patient names are replaced with role labels before sending.
            Clinical condition data is included to enable AI responses. Cross-border transfer to USA.{' '}
            <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>Privacy policy</a></li>
          <li><strong>OpenAI Whisper</strong> (USA) — Voice transcription for healer chakra scans.
            Receives: audio recordings. Audio is not stored permanently. Cross-border transfer to USA.{' '}
            <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer" style={styles.link}>Privacy policy</a></li>
          <li><strong>Razorpay</strong> (Razorpay Software Pvt Ltd, India) — Payment processing for
            subscriptions, session packages, and healer earnings payouts. Receives: name, email, phone,
            payment details, and healer bank account or UPI details for payout processing.{' '}
            <a href="https://razorpay.com/privacy/" target="_blank" rel="noopener noreferrer" style={styles.link}>Privacy policy</a></li>
          <li><strong>Meta</strong> (Meta Platforms Inc., USA) — WhatsApp Business API for appointment
            and session notifications. Receives: patient and healer mobile numbers. Cross-border
            transfer to USA.{' '}
            <a href="https://www.whatsapp.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" style={styles.link}>Privacy policy</a></li>
          <li><strong>Brevo</strong> (Sendinblue SAS, France) — Transactional email for invoices,
            session reminders, and account notifications. Receives: name and email address.{' '}
            <a href="https://www.brevo.com/legal/privacypolicy/" target="_blank" rel="noopener noreferrer" style={styles.link}>Privacy policy</a></li>
          <li><strong>MongoDB Atlas</strong> (MongoDB Inc., USA) — Encrypted cloud database hosted
            in Singapore (AWS ap-southeast-1). All data encrypted at rest and in transit.{' '}
            <a href="https://www.mongodb.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" style={styles.link}>Privacy policy</a></li>
          <li><strong>Render</strong> (Render Services Inc., USA) — Cloud hosting for the LifeChiAI
            backend API. Processes all data transiting the API. Cross-border transfer to USA.{' '}
            <a href="https://render.com/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>Privacy policy</a></li>
          <li><strong>ElevenLabs</strong> (ElevenLabs Inc., USA) — AI voice synthesis for audio
            content. Receives: text prompts for voice generation. Cross-border transfer to USA.{' '}
            <a href="https://elevenlabs.io/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>Privacy policy</a></li>
          <li><strong>Cloudflare R2</strong> (Cloudflare Inc., USA) — Object storage for uploaded
            documents and certification files. Receives: uploaded file content. Cross-border
            transfer to USA.{' '}
            <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" style={styles.link}>Privacy policy</a></li>
          <li><strong>Fast2SMS</strong> (India) — SMS OTP delivery for login and authentication.
            Receives: mobile phone number to deliver one-time passwords.{' '}
            <a href="https://www.fast2sms.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={styles.link}>Privacy policy</a></li>
          <li><strong>Apple Inc.</strong> (USA) — Sign in with Apple identity verification.
            Receives: name and relay email during authentication. Cross-border transfer to USA.{' '}
            <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer" style={styles.link}>Privacy policy</a></li>
          <li><strong>Google LLC</strong> (USA) — Google Sign-In identity verification. Receives:
            email, display name, and profile during token issuance. Cross-border transfer to USA.{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>Privacy policy</a></li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2 style={styles.subheading}>5. Your rights</h2>
        <ul style={styles.list}>
          <li><strong>Access:</strong> Request a copy of all data we hold about you</li>
          <li><strong>Correction:</strong> Request correction of inaccurate data</li>
          <li><strong>Withdrawal of consent:</strong> Withdraw consent at any time from your profile settings — we will stop processing your data for new activities</li>
          <li><strong>Erasure:</strong> Request deletion of your personal data (healing history is retained as required by applicable law)</li>
          <li><strong>Grievance:</strong> Raise a complaint with the Data Protection Board of India</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2 style={styles.subheading}>6. Data retention</h2>
        <p style={styles.body}>
          Your healing records are retained for 7 years as required by applicable health regulations.
          Account data is deleted within 30 days of an erasure request, subject to legal retention
          obligations.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.subheading}>7. Contact</h2>
        <p style={styles.body}>
          For privacy requests or grievances, contact our Data Protection Officer at{' '}
          <a href="mailto:privacy@lifechiai.com" style={styles.link}>privacy@lifechiai.com</a>.
          We respond within 72 hours.
        </p>
      </section>

      <div style={styles.backWrap}>
        <a href="/login" style={styles.back}>Back to Login</a>
      </div>
    </div>
  </div>
);

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #071e12 0%, #0a1f14 30%, #0c2617 60%, #071e12 100%)',
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 20px',
    fontFamily: "'Open Sans', sans-serif"
  },
  card: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '16px',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '720px',
    backdropFilter: 'blur(20px)',
    color: 'rgba(255,255,255,0.85)'
  },
  heading: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '28px',
    fontWeight: '800',
    color: '#8DC63F',
    marginBottom: '6px'
  },
  meta: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.4)',
    marginBottom: '36px'
  },
  section: {
    marginBottom: '28px'
  },
  subheading: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '16px',
    fontWeight: '700',
    color: '#8DC63F',
    marginBottom: '10px'
  },
  body: {
    fontSize: '14px',
    lineHeight: '1.7',
    color: 'rgba(255,255,255,0.75)',
    margin: '0 0 10px 0'
  },
  list: {
    fontSize: '14px',
    lineHeight: '1.8',
    color: 'rgba(255,255,255,0.75)',
    paddingLeft: '20px',
    margin: '0 0 10px 0'
  },
  subheading3: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '14px',
    fontWeight: '700',
    color: 'rgba(255,255,255,0.7)',
    marginBottom: '8px',
    marginTop: '16px'
  },
  link: {
    color: '#8DC63F',
    textDecoration: 'underline'
  },
  backWrap: {
    marginTop: '36px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    paddingTop: '24px'
  },
  back: {
    fontSize: '13px',
    color: '#8DC63F',
    textDecoration: 'none',
    fontFamily: "'Raleway', sans-serif",
    fontWeight: '700'
  }
};

export default Privacy;
